import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Heading from "@amzn/meridian/heading";
import Loader from "@amzn/meridian/loader";
import Alert from "@amzn/meridian/alert";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Text from "@amzn/meridian/text";
import PorApiFactory from "../../por-api/PlanOfRecordApiFactory";
import { ActiveSiteData, UpdateActiveSitesRequest } from "../../por-api/generated-src";
import { useBulkEditContext, BulkEditRow, EditableFieldData } from "../../store/bulk-edit-store";
import { usePORContext } from "../../store/por-context";
import { Logger } from "@amzn/kepler-katal-logger";
import { ALERT_AUTHENTICATION_MESSAGE, POR_SUPER_ADMIN } from "../../constants";
import { BULK_EDIT_IDENTIFIER_COLUMNS, BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS, FIELD_GROUP_BUILDING_ATTRIBUTES, FIELD_GROUP_PRODUCTIVITY } from "./BulkEditConstants";
import { constructBulkEditPayload, batchChangeBody } from "./BulkEditSaveManager";
import BulkEditTable from "./BulkEditTable";
import BulkEditPagination from "./BulkEditPagination";
import BulkEditColumnSelector from "./BulkEditColumnSelector";
import BulkEditNavigationGuard from "./BulkEditNavigationGuard";
import ConfirmChangeModal from "../modal/ConfirmChangeModal";
import ToasterPORResponse from "../toaster/ToasterPORResponse";
import "./BulkEditPage.scss";

export default function BulkEditPage() {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { user, onOpenToast } = usePORContext();
  const {
    rows,
    setRows,
    originalRows,
    setOriginalRows,
    visibleColumns,
    setVisibleColumns,
    isLoading,
    setIsLoading,
    isSaving,
    setIsSaving,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    cellStates,
    setCellStates,
  } = useBulkEditContext();

  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showConfirmChangeModal, setShowConfirmChangeModal] = useState(false);
  const [showNavigateAwayDialog, setShowNavigateAwayDialog] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");

  const PorApi = PorApiFactory();
  const isSuperAdmin = user === POR_SUPER_ADMIN;

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    const selectedProjectIds: string[] = locationState?.selectedProjectIds || [];
    if (!selectedProjectIds.length) {
      navigate("/active-projects");
      return;
    }

    loadProjectData(selectedProjectIds);
  }, []);

  const loadProjectData = async (projectIds: string[]) => {
    setIsLoading(true);
    try {
      const projectPromises = projectIds.map((id) => PorApi.getActiveSite(id));
      const responses = await Promise.all(projectPromises);
      const bulkEditRows: BulkEditRow[] = responses.map((response) => {
        const projectData = response.data as ActiveSiteData;
        return transformToRow(projectData);
      });

      setRows(bulkEditRows);
      setOriginalRows(bulkEditRows);

      // Set all editable columns visible by default, plus optional identifier columns
      if (bulkEditRows.length > 0) {
        const allEditableColumns = Object.keys(bulkEditRows[0].editableFields);
        setVisibleColumns([...BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS, ...allEditableColumns]);
      }
    } catch (ex: any) {
      if (ex.response?.status === 403) {
        setErrorMessage(ALERT_AUTHENTICATION_MESSAGE);
      } else {
        setErrorMessage(ex.message || "Failed to load project data.");
        Logger.getInstance().logError(ex);
      }
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const transformToRow = (projectData: ActiveSiteData): BulkEditRow => {
    const identifierFields: Record<string, string> = {};
    const editableFields: Record<string, EditableFieldData> = {};

    // Search ALL field groups for identifier columns (both fixed and optional)
    const allFieldGroups = [
      ...(projectData.siteDetails || []),
      ...(projectData.buildingAttributes || []),
      ...(projectData.productivity || []),
      ...(projectData.additionalDetails || []),
      ...(projectData.retrofitDetails || []),
    ];

    const allIdentifierColumns = [...BULK_EDIT_IDENTIFIER_COLUMNS, ...BULK_EDIT_OPTIONAL_IDENTIFIER_COLUMNS];

    for (const field of allFieldGroups) {
      if (allIdentifierColumns.includes(field.fieldName as any)) {
        identifierFields[field.fieldName || ""] = field.value || "";
      }
    }

    // Extract editable fields from buildingAttributes
    for (const field of projectData.buildingAttributes || []) {
      const name = field.fieldName || "";
      if (allIdentifierColumns.includes(name as any)) continue;
      editableFields[name] = {
        value: field.value || "",
        originalValue: field.value || "",
        fieldType: (field.type || "text").toLowerCase(),
        required: field.required || false,
        options: field.options,
        fieldGroup: FIELD_GROUP_BUILDING_ATTRIBUTES,
      };
    }

    // Extract editable fields from productivity
    for (const field of projectData.productivity || []) {
      const name = field.fieldName || "";
      if (allIdentifierColumns.includes(name as any)) continue;
      editableFields[name] = {
        value: field.value || "",
        originalValue: field.value || "",
        fieldType: (field.type || "text").toLowerCase(),
        required: field.required || false,
        options: field.options,
        fieldGroup: FIELD_GROUP_PRODUCTIVITY,
      };
    }

    return {
      projectId: projectData.projectId || "",
      identifierFields,
      editableFields,
    };
  };

  const navigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigateAwayDialog(true);
    } else {
      navigate("/active-projects");
    }
  };

  const confirmNavigateAway = () => {
    setShowNavigateAwayDialog(false);
    navigate("/active-projects");
  };

  const handleSaveClick = () => {
    const { changedRowCount, changedCellCount } = constructBulkEditPayload(rows, originalRows);
    if (changedRowCount === 0) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    setShowConfirmDialog(false);
    setShowConfirmChangeModal(true);
  };

  const submitChangeHandler = (linksList, reason, team, additionalNotes) => async () => {
    setIsSaving(true);
    const { changeBody, changedRowCount } = constructBulkEditPayload(rows, originalRows);
    const batches = batchChangeBody(changeBody);

    try {
      for (let i = 0; i < batches.length; i++) {
        const payload: UpdateActiveSitesRequest = {
          changeReason: {
            reasonForChange: reason,
            approvalLinks: linksList,
            isSuperAdmin: true,
            team: team,
            additionalNotes: additionalNotes,
          },
          changeBody: batches[i],
        };
        await PorApi.updateActiveSites(payload);
      }

      setToasterMessage(`Bulk edit saved successfully (${changedRowCount} projects updated)`);
      onOpenToast();
      setCellStates({});
      setHasUnsavedChanges(false);
      setOriginalRows(rows);
    } catch (ex: any) {
      if (ex.response?.status === 403) {
        setErrorMessage(ALERT_AUTHENTICATION_MESSAGE);
      } else {
        setErrorMessage(
          `Some projects may have saved successfully. Check the Change History tab. Error: ${ex.message || "Unknown error"}`
        );
      }
      setShowAlert(true);
      Logger.getInstance().logError(ex);
    } finally {
      setIsSaving(false);
      setShowConfirmChangeModal(false);
    }
  };

  const onConfirmCloseChangeModal = useCallback(() => {
    setShowConfirmChangeModal(false);
  }, []);

  const { changedRowCount, changedCellCount } = useMemo(
    () => constructBulkEditPayload(rows, originalRows),
    [rows, originalRows]
  );

  if (!isSuperAdmin) {
    return (
      <Column width="100%" spacingInset="400">
        <Alert type="error">
          You do not have permission to access Bulk Edit. Super Admin access is required.
        </Alert>
      </Column>
    );
  }

  if (isLoading) {
    return (
      <Column width="100%" spacingInset="400" alignmentHorizontal="center">
        <Loader size="large" label="Loading project data..." />
      </Column>
    );
  }

  return (
    <Column width="100%" spacingInset="400" className="bulk-edit-page">
      {showAlert && (
        <Alert type="error" onClose={() => setShowAlert(false)}>
          {errorMessage}
        </Alert>
      )}
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Row alignmentVertical="center">
          <Heading level={4}>Bulk Edit ({rows.length} projects)</Heading>
        </Row>
        <Row>
          <Button type="secondary" onClick={() => setIsColumnSelectorOpen(true)}>
            Column Filter
          </Button>
          <Button
            type="primary"
            onClick={handleSaveClick}
            disabled={!hasUnsavedChanges || isSaving}
          >
            {isSaving ? <Loader size="small" /> : "Save Bulk Edit"}
          </Button>
          <Button type="tertiary" onClick={navigateBack}>
            Return to Projects
          </Button>
        </Row>
      </Row>
      <BulkEditTable />
      <BulkEditPagination />
      <BulkEditColumnSelector isOpen={isColumnSelectorOpen} setIsOpen={setIsColumnSelectorOpen} />
      {/* Confirmation dialog */}
      <Modal
        open={showConfirmDialog}
        title="Confirm Save"
        onClose={() => setShowConfirmDialog(false)}
        scrollContainer="viewport"
      >
        <Text>
          Are you sure you want to save? {changedRowCount} out of {rows.length} rows changed, {changedCellCount} cells modified.
        </Text>
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button type="secondary" size="small" onClick={() => setShowConfirmDialog(false)}>
              No
            </Button>
            <Button type="primary" size="small" onClick={handleConfirmSave}>
              Yes
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
      {/* Change Request Modal (reuse existing) */}
      <ConfirmChangeModal
        open={showConfirmChangeModal}
        onClose={onConfirmCloseChangeModal}
        confirmHandler={submitChangeHandler}
      />
      <ToasterPORResponse message={toasterMessage} />
      <BulkEditNavigationGuard hasUnsavedChanges={hasUnsavedChanges} />
      {/* Navigate away confirmation */}
      <Modal
        open={showNavigateAwayDialog}
        title=""
        onClose={() => setShowNavigateAwayDialog(false)}
        scrollContainer="viewport"
      >
        <Column>
          <Heading level={4}>Unsaved Changes</Heading>
          <Text>
            You have unsaved changes that will be lost if you navigate away. Are you sure you want to leave?
          </Text>
        </Column>
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button type="secondary" size="small" onClick={() => setShowNavigateAwayDialog(false)}>
              Stay
            </Button>
            <Button type="primary" size="small" onClick={confirmNavigateAway}>
              Leave
            </Button>
          </Row>
        </ModalFooter>
      </Modal>
    </Column>
  );
}
